/* eslint-disable react/display-name */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useEffect } from 'react';
import { Typography } from '@material-ui/core';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { merge } from 'lodash';
import { TFunction } from 'i18next';

import WDT_CLASSROOM_PROGRESS_BY_CHARACTERS_QUERY from 'graph/reports/wdt-classroom-progress-by-characters-report-query';
import useReportQueryParams from '../../../../hooks/use-report-query-params';
import ReportCard from 'components/ReportsModule/components/common/ReportCard';
import LwtTable, { ColumnProps } from 'components/@common/Table/LwtTable';
import { ModuleType } from '../../../../types';
import { getQueryVariables } from '../../../../utils/report-param-utils';
import { WdtClassroomProgressByCharactersData } from 'types/schema-generated-types';

const getColumns: (t: TFunction) => ColumnProps<any>[] = t => [
  {
    id: 'character',
    label: t('Character'),
    sortKey: 'character',
    getValue: item => <Typography>{item.character}</Typography>,
    sortable: true
  },
  {
    id: 'level1',
    label: t('Completed 1 Star'),
    sortKey: 'level1',
    getValue: item => <Typography>{item.level1}</Typography>,
    sortable: true
  },
  {
    id: 'level2',
    label: t('Completed 2 Star'),
    sortKey: 'level2',
    getValue: item => <Typography>{item.level2}</Typography>,
    sortable: true
  },
  {
    id: 'level3',
    label: t('Completed 3 Star'),
    sortKey: 'level3',
    getValue: item => <Typography>{item.level3}</Typography>,
    sortable: true
  }
];

interface ProgressForCharactersTableViewProps {
  module: ModuleType;
}

const ProgressForCharactersTableView: React.FC<ProgressForCharactersTableViewProps> = ({
  module
}) => {
  const [{ dateRange, entityType, entityId }] = useReportQueryParams();
  const [fetchReport, { data, loading }] = useLazyQuery(WDT_CLASSROOM_PROGRESS_BY_CHARACTERS_QUERY);
  const { t } = useTranslation('Reports');

  const columns = getColumns(t);

  const reportData: WdtClassroomProgressByCharactersData[] =
    data?.wdtClassroomProgressByCharactersReport?.characterProgress ?? [];

  const mappedReportData = reportData.map(item => ({
    character: item.character.character,
    level1: item.characterLevelsProgress.find(item => item.level === 1)?.studentCompletedCount ?? 0,
    level2: item.characterLevelsProgress.find(item => item.level === 2)?.studentCompletedCount ?? 0,
    level3: item.characterLevelsProgress.find(item => item.level === 3)?.studentCompletedCount ?? 0
  }));

  useEffect(() => {
    fetchReport({
      variables: merge(getQueryVariables({ entityId, entityType, dateRange }), {
        where: {
          module: module,
          characterOrder: 'natural'
        }
      })
    });
  }, [fetchReport, entityId, entityType, dateRange, module]);

  return (
    <ReportCard
      title={t(`Progress for ${t(module, { ns: 'ModuleType' })}`)}
      loading={loading}
      reportData={reportData}
    >
      <LwtTable columns={columns} data={mappedReportData} />
    </ReportCard>
  );
};

export default ProgressForCharactersTableView;
